import { FormEvent, useState } from 'react'
import { UserPlus } from 'lucide-react'
import { Alerta, Badge, Boton, Campo, Cargando, Casilla, Entrada, Interruptor, Panel, Tabla } from '../../components/kit'
import { useAuth } from '../../contexts/AuthProvider'
import { useCambiarUsuario, useRegistrarUsuario, useUsuarios } from '../../queries/useUsuarios'
import { RolApp } from '../../types/auth'
import { ROLES, TODOS_LOS_ROLES } from '../../utils/roles'
import { mensajeDeError } from '../../utils/errores'
import { formatoFecha } from '../../utils/fecha'

/**
 * Quién puede entrar y qué puede hacer (solo administración). La app no crea cuentas al
 * entrar por el portal: aquí se registra a la persona por RUT, con sus roles, y desde ese
 * momento puede ingresar con su U-Pasaporte. Nadie se quita el acceso ni la
 * administración a sí mismo. Un superusuario tiene todos los roles.
 */
export default function Usuarios() {
  const { user } = useAuth()
  const usuarios = useUsuarios()
  const registrar = useRegistrarUsuario()
  const cambiar = useCambiarUsuario()
  const [rut, setRut] = useState('')
  const [email, setEmail] = useState('')
  const [admin, setAdmin] = useState(false)
  const [roles, setRoles] = useState<RolApp[]>([])
  const alternar = (lista: RolApp[], rol: RolApp) => (lista.includes(rol) ? lista.filter((r) => r !== rol) : [...lista, rol])

  const guardar = (e: FormEvent) => {
    e.preventDefault()
    registrar.mutate(
      { rut, email, es_staff: admin, roles },
      {
        onSuccess: () => {
          setRut('')
          setEmail('')
          setAdmin(false)
          setRoles([])
        },
      },
    )
  }

  return (
    <div className="pila pila--amplia">
      <header className="pagina__cabecera">
        <h1 className="dcc-t-heading-m">Usuarios</h1>
        <p className="texto-secundario">
          Solo entran las personas registradas aquí. Lo que cada una puede hacer lo dicen sus roles; administración además gestiona usuarios.
        </p>
      </header>

      <Panel titulo="Registrar usuario">
        <form className="pila" onSubmit={guardar}>
          <div className="formulario-grilla">
            <Campo etiqueta="RUT" requerido>
              <Entrada value={rut} onChange={(e) => setRut(e.target.value)} placeholder="12.345.678-5" required autoComplete="off" />
            </Campo>
            <Campo etiqueta="Correo" ayuda="Opcional: el portal lo trae al primer ingreso.">
              <Entrada type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Campo>
          </div>
          <fieldset className="opciones">
            <legend className="dcc-field__label">Roles</legend>
            {TODOS_LOS_ROLES.map((r) => (
              <Casilla
                key={r}
                etiqueta={<>{ROLES[r].texto} <span className="texto-secundario">· {ROLES[r].detalle}</span></>}
                checked={roles.includes(r)}
                onChange={() => setRoles(alternar(roles, r))}
              />
            ))}
          </fieldset>
          <Casilla etiqueta="Es de administración (gestiona usuarios)" checked={admin} onChange={(e) => setAdmin(e.target.checked)} />
          {registrar.isError && <Alerta tipo="error">{mensajeDeError(registrar.error)}</Alerta>}
          {registrar.isSuccess && <Alerta tipo="success">{registrar.data.nombre || registrar.data.username} ya puede entrar.</Alerta>}
          <Boton type="submit" icono={UserPlus} cargando={registrar.isPending}>Registrar</Boton>
        </form>
      </Panel>

      {usuarios.isLoading && <Cargando />}
      {usuarios.isError && <Alerta tipo="error">{mensajeDeError(usuarios.error)}</Alerta>}
      {cambiar.isError && <Alerta tipo="error">{mensajeDeError(cambiar.error)}</Alerta>}
      {usuarios.data && (
        <Tabla etiqueta="Usuarios" apilable>
          <thead>
            <tr>
              <th scope="col">Persona</th>
              <th scope="col">Correo</th>
              <th scope="col">Acceso</th>
              <th scope="col">Roles</th>
              <th scope="col">Administración</th>
              <th scope="col">Último ingreso</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.data.map((u) => {
              const yo = u.username === user?.username
              return (
                <tr key={u.id}>
                  <td className="tabla__principal tabla__texto">
                    {u.nombre || u.username}
                    <span className="tabla__sub">{u.rut_formateado || u.username}{yo && ' · tú'}</span>
                  </td>
                  <td data-etiqueta="Correo" className="tabla__texto">{u.email || '—'}</td>
                  <td data-etiqueta="Acceso">
                    <Interruptor
                      etiqueta={u.activo ? 'Puede entrar' : 'Sin acceso'}
                      checked={u.activo}
                      disabled={yo || cambiar.isPending}
                      onChange={(e) => cambiar.mutate({ id: u.id, activo: e.target.checked })}
                    />
                  </td>
                  <td data-etiqueta="Roles">
                    {u.superusuario ? (
                      <Badge tono="marca">Todos</Badge>
                    ) : (
                      <div className="roles-usuario">
                        {TODOS_LOS_ROLES.map((r) => (
                          <Casilla
                            key={r}
                            etiqueta={ROLES[r].texto}
                            checked={u.roles.includes(r)}
                            disabled={cambiar.isPending}
                            onChange={() => cambiar.mutate({ id: u.id, roles: alternar(u.roles, r) })}
                          />
                        ))}
                      </div>
                    )}
                  </td>
                  <td data-etiqueta="Administración">
                    {u.superusuario ? (
                      <Badge tono="marca">Superusuario</Badge>
                    ) : (
                      <Interruptor
                        etiqueta={u.es_staff ? 'Sí' : 'No'}
                        checked={u.es_staff}
                        disabled={yo || cambiar.isPending}
                        onChange={(e) => cambiar.mutate({ id: u.id, es_staff: e.target.checked })}
                      />
                    )}
                  </td>
                  <td data-etiqueta="Último ingreso">{formatoFecha(u.ultimo_ingreso)}</td>
                </tr>
              )
            })}
          </tbody>
        </Tabla>
      )}
    </div>
  )
}
